import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  list,
  changeEventName,
  deleteEvent,
} from "../../../services/eventService";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { getTableEmptyProps } from "../../common/supplierTableEmpty";
import {
  SupplierAddButton,
  SupplierFilterToolbar,
  SupplierFilterField,
  SupplierPageLoading,
} from "../../common/supplierPageUi";

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const Events = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [eventId, setEventId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");
  const [isNameEditing, setIsNameEditing] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const queryClient = useQueryClient();
  const pageMeta = SUPPLIER_PAGE_META.events;

  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["events", currentPage, debouncedSearchTerm, filterCategory],
    queryFn: () => list(currentPage, debouncedSearchTerm, filterCategory),
    keepPreviousData: true,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const events = Array.isArray(response?.data) ? response?.data : [];

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setFilterCategory("");
    setCurrentPage(1);
  }, []);

  const current_page = response?.current_page || 1;
  const last_page = response?.last_page || 1;
  const total = response?.total || 0;
  const per_page = response?.per_page || 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNameUpdate = async (id, newName) => {
    if (!newName.trim()) {
      setUpdateError({ id, message: "Event name cannot be empty" });
      return;
    }

    try {
      setUpdateLoading(true);
      setUpdateError(null);
      await changeEventName(id, newName);
      await queryClient.invalidateQueries(["events"]);
      setEventId(null);
      setToasterMessage("Event name updated successfully!");
      setToasterType("success");
      setShowToaster(true);
    } catch (err) {
      setUpdateError({
        id,
        message: err.response?.data?.message || "Failed to update event name",
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteEvent = async (eventIdToDelete, eventName) => {
    if (!window.confirm(`Are you sure you want to delete "${eventName}"?`)) {
      return;
    }

    try {
      await deleteEvent(eventIdToDelete);
      await queryClient.invalidateQueries(["events"]);
      setToasterMessage("Event deleted successfully!");
      setToasterType("success");
      setShowToaster(true);
    } catch (err) {
      setToasterMessage(
        err.response?.data?.message || "Failed to delete event"
      );
      setToasterType("error");
      setShowToaster(true);
    }
  };

  const add = () => {
    navigate("/events/new");
  };

  const renderStatus = (event) => {
    if (event.is_draft === 1) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Draft
        </span>
      );
    }
    if (event.is_approved === 0) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    }
    if (event.is_approved === 1) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Approved
        </span>
      );
    }
    if (event.is_approved === 2) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Unknown
      </span>
    );
  };

  const hasActiveFilters = searchTerm || filterCategory;

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
        actions={<SupplierAddButton onClick={add}>Add Event</SupplierAddButton>}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar
            showReset={hasActiveFilters}
            onReset={clearFilters}
          >
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search events..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        {isLoading && !isSearching ? (
          <SupplierPageLoading message="Loading events..." />
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading events
              </h3>
              <p className="text-gray-600">{error.message}</p>
            </div>
          </div>
        ) : (
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              <AdminTableFooter>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </AdminTableFooter>
            }
          >
            <AdminTableHead>
              <AdminTableTh>#</AdminTableTh>
              <AdminTableTh>Event Name</AdminTableTh>
              <AdminTableTh>Category</AdminTableTh>
              <AdminTableTh>Duration</AdminTableTh>
              <AdminTableTh>Location</AdminTableTh>
              <AdminTableTh>Status</AdminTableTh>
              <AdminTableTh>Created On</AdminTableTh>
              <AdminTableTh align="right">Actions</AdminTableTh>
            </AdminTableHead>
            <AdminTableBody>
              {events.length === 0 ? (
                <TableEmptyRow
                  colSpan={8}
                  {...getTableEmptyProps("events", {
                    onAdd: add,
                    hasActiveFilters: !!(searchTerm || filterCategory),
                    onClearFilters: clearFilters,
                  })}
                />
              ) : (
                events.map((event, index) => (
                  <AdminTableRow key={event.id}>
                    <AdminTableCell>
                      {(current_page - 1) * per_page + index + 1}
                    </AdminTableCell>
                    <AdminTableCell className="font-medium">
                      {isNameEditing && eventId === event.id ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onBlur={() => {
                                if (!updateLoading) {
                                  handleNameUpdate(event.id, editingName);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleNameUpdate(event.id, editingName);
                                } else if (e.key === "Escape") {
                                  setEventId(null);
                                  setUpdateError(null);
                                }
                              }}
                              className={`w-full px-2 py-1 border rounded focus:outline-none focus:border-primary ${
                                updateError?.id === event.id
                                  ? "border-red-500"
                                  : "border-gray-200"
                              }`}
                              disabled={updateLoading}
                              autoFocus
                            />
                            {updateLoading && eventId === event.id && (
                              <i className="fi fi-rr-spinner animate-spin text-primary" />
                            )}
                          </div>
                          {updateError?.id === event.id && (
                            <p className="text-xs text-red-500">
                              {updateError.message}
                            </p>
                          )}
                        </div>
                      ) : (
                        event.name || event.title || "Untitled Event"
                      )}
                    </AdminTableCell>
                    <AdminTableCell>
                      {event.event_category_master?.name || "N/A"}
                    </AdminTableCell>
                    <AdminTableCell>{event.duration || "N/A"}</AdminTableCell>
                    <AdminTableCell>
                      {event.location?.address || event.location || "N/A"}
                    </AdminTableCell>
                    <AdminTableCell>{renderStatus(event)}</AdminTableCell>
                    <AdminTableCell>
                      {formatListingDateTime(event.created_at)}
                    </AdminTableCell>
                    <TableActionsCell>
                      <EditActionButton
                        showLabel={false}
                        onClick={() => navigate(`/events/${event.id}/edit`)}
                      />
                      <DeleteActionButton
                        showLabel={false}
                        onClick={() =>
                          handleDeleteEvent(
                            event.id,
                            event.name || event.title || "this event"
                          )
                        }
                      />
                    </TableActionsCell>
                  </AdminTableRow>
                ))
              )}
            </AdminTableBody>
          </AdminTable>
        )}
      </AdminPageCard>

      {showToaster && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          duration={3000}
          onClose={() => setShowToaster(false)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Events;
